import { useEffect, useState } from "react";
import axios from "axios";
import { LOGO_URL } from "@/constants/site";
import { Lock, LogOut, Check, Trash2 } from "lucide-react";
import { HouseLevelBadge } from "@/components/HouseRating";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = "tbd-admin-token";

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [password, setPassword] = useState("");
  const [enquiries, setEnquiries] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [tab, setTab] = useState("enquiries");
  const [loading, setLoading] = useState(false);
  const [listLoading, setListLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = async (tok) => {
    setListLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/enquiries`, { headers: { Authorization: `Bearer ${tok}` } });
      setEnquiries(res.data);
    } catch (e) {
      if (e.response?.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        setToken("");
      } else {
        setError("Could not load enquiries.");
      }
    } finally {
      setListLoading(false);
    }
  };

  useEffect(() => {
    if (token) {
      load(token);
      loadReviews(token);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadReviews = async (tok) => {
    try {
      const res = await axios.get(`${API}/admin/reviews`, { headers: { Authorization: `Bearer ${tok}` } });
      setReviews(res.data);
    } catch (e) {
      /* enquiries error handling covers session expiry */
    }
  };

  const approveReview = async (id) => {
    await axios.post(`${API}/admin/reviews/${id}/approve`, {}, { headers: { Authorization: `Bearer ${token}` } });
    loadReviews(token);
  };

  const deleteReview = async (id) => {
    await axios.delete(`${API}/admin/reviews/${id}`, { headers: { Authorization: `Bearer ${token}` } });
    loadReviews(token);
  };

  const deleteEnquiry = async (id) => {
    await axios.delete(`${API}/enquiries/${id}`, { headers: { Authorization: `Bearer ${token}` } });
    load(token);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const res = await axios.post(`${API}/admin/login`, { password });
      localStorage.setItem(TOKEN_KEY, res.data.token);
      setToken(res.data.token);
      setPassword("");
      load(res.data.token);
      loadReviews(res.data.token);
    } catch (err) {
      setError("Wrong password. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setEnquiries([]);
  };

  if (!token) {
    return (
      <div className="bg-[#1A1412] min-h-screen text-[#FDFBF7] flex items-center justify-center px-6" data-testid="admin-login-page">
        <form onSubmit={handleLogin} className="w-full max-w-sm bg-[#2A211D] border border-[#D4AF37]/20 p-10 text-center" data-testid="admin-login-form">
          <img src={LOGO_URL} alt="The Build and Design logo" className="h-16 w-16 object-cover rounded-sm mx-auto" />
          <h1 className="font-heading text-2xl text-[#D4AF37] mt-6">Owner Login</h1>
          <p className="text-[#B8A99A] text-sm mt-2">Enter your admin password to view enquiries</p>
          <div className="mt-8 flex items-center gap-3 bg-[#1A1412] border border-[#D4AF37]/25 px-4">
            <Lock className="h-4 w-4 text-[#D4AF37] shrink-0" />
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Admin password"
              className="w-full bg-transparent py-3.5 text-sm text-[#FDFBF7] placeholder-[#B8A99A]/60 focus:outline-none"
              data-testid="admin-password-input"
            />
          </div>
          {error && <p className="text-red-400 text-sm mt-4" data-testid="admin-login-error">{error}</p>}
          <button
            type="submit"
            disabled={loading}
            className="w-full mt-6 bg-[#D4AF37] text-[#1A1412] py-3.5 text-sm tracking-widest uppercase font-semibold hover:bg-[#E5C15D] transition-colors duration-300 disabled:opacity-60"
            data-testid="admin-login-button"
          >
            {loading ? "Checking..." : "Login"}
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="bg-[#1A1412] min-h-screen text-[#FDFBF7]" data-testid="admin-page">
      <header className="border-b border-[#D4AF37]/20 px-6 lg:px-12 py-6 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <img src={LOGO_URL} alt="The Build and Design logo" className="h-10 w-10 object-cover rounded-sm" />
          <h1 className="font-heading text-2xl text-[#D4AF37]">Enquiries</h1>
        </div>
        <button
          onClick={handleLogout}
          className="flex items-center gap-2 border border-[#D4AF37]/60 text-[#D4AF37] px-4 py-2 text-xs tracking-widest uppercase hover:bg-[#D4AF37] hover:text-[#1A1412] transition-colors duration-300"
          data-testid="admin-logout-button"
        >
          <LogOut className="h-4 w-4" /> Logout
        </button>
      </header>
      <main className="max-w-7xl mx-auto px-6 lg:px-12 py-12">
        <div className="flex gap-3 mb-10">
          <button
            onClick={() => setTab("enquiries")}
            className={`px-6 py-2.5 text-xs tracking-widest uppercase transition-colors duration-300 border ${tab === "enquiries" ? "bg-[#D4AF37] text-[#1A1412] border-[#D4AF37] font-semibold" : "border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/10"}`}
            data-testid="admin-tab-enquiries"
          >
            Enquiries
          </button>
          <button
            onClick={() => setTab("reviews")}
            className={`px-6 py-2.5 text-xs tracking-widest uppercase transition-colors duration-300 border ${tab === "reviews" ? "bg-[#D4AF37] text-[#1A1412] border-[#D4AF37] font-semibold" : "border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/10"}`}
            data-testid="admin-tab-reviews"
          >
            Reviews
          </button>
        </div>

        {tab === "reviews" && (
          <div data-testid="admin-reviews-section">
            {reviews.length === 0 && <p className="text-[#B8A99A]" data-testid="admin-reviews-empty">No reviews yet.</p>}
            <div className="space-y-4" data-testid="admin-reviews-list">
              {reviews.map((r) => (
                <div key={r.id} className="border border-[#D4AF37]/20 bg-[#2A211D] p-6" data-testid={`admin-review-${r.id}`}>
                  <div className="flex flex-wrap justify-between gap-2 items-center">
                    <span className="font-heading text-xl text-[#D4AF37]">{r.name}</span>
                    <HouseLevelBadge value={r.rating} />
                  </div>
                  {r.project && <p className="mt-1 text-xs text-[#B8A99A]">{r.project}</p>}
                  <p className="mt-3 text-sm text-[#FDFBF7]">"{r.text}"</p>
                  <div className="mt-4 flex items-center gap-3">
                    <span className={`text-xs tracking-widest uppercase ${r.approved ? "text-green-400" : "text-yellow-400"}`} data-testid={`admin-review-status-${r.id}`}>
                      {r.approved ? "Live on website" : "Pending approval"}
                    </span>
                    {!r.approved && (
                      <button
                        onClick={() => approveReview(r.id)}
                        className="flex items-center gap-2 bg-[#D4AF37] text-[#1A1412] px-4 py-2 text-xs tracking-widest uppercase font-semibold hover:bg-[#E5C15D] transition-colors"
                        data-testid={`admin-review-approve-${r.id}`}
                      >
                        <Check className="h-3.5 w-3.5" /> Approve
                      </button>
                    )}
                    <button
                      onClick={() => deleteReview(r.id)}
                      className="flex items-center gap-2 border border-red-400/50 text-red-400 px-4 py-2 text-xs tracking-widest uppercase hover:bg-red-400/10 transition-colors"
                      data-testid={`admin-review-delete-${r.id}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" /> Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "enquiries" && (
          <>
        {listLoading && <p className="text-[#B8A99A]" data-testid="admin-loading">Loading...</p>}
        {error && <p className="text-red-400" data-testid="admin-error">{error}</p>}
        {!listLoading && !error && enquiries.length === 0 && (
          <p className="text-[#B8A99A]" data-testid="admin-empty">No enquiries yet.</p>
        )}
        {!listLoading && !error && enquiries.length > 0 && (
          <div className="space-y-4" data-testid="admin-enquiries-list">
            {enquiries.map((e) => (
              <div key={e.id} className="border border-[#D4AF37]/20 bg-[#2A211D] p-6" data-testid={`admin-enquiry-${e.id}`}>
                <div className="flex flex-wrap justify-between gap-2">
                  <span className="font-heading text-xl text-[#D4AF37]">{e.name}</span>
                  <span className="text-xs text-[#B8A99A]">{new Date(e.created_at).toLocaleString()}</span>
                </div>
                <p className="mt-2 text-sm text-[#FDFBF7]">{e.phone} — {e.service}</p>
                {e.site_visit && <p className="mt-1 text-xs text-[#D4AF37] tracking-widest uppercase">Free site visit requested</p>}
                {e.message && <p className="mt-3 text-sm text-[#B8A99A]">{e.message}</p>}
                <div className="mt-4">
                  <button
                    onClick={() => deleteEnquiry(e.id)}
                    className="flex items-center gap-2 border border-red-400/50 text-red-400 px-4 py-2 text-xs tracking-widest uppercase hover:bg-red-400/10 transition-colors"
                    data-testid={`admin-enquiry-delete-${e.id}`}
                  >
                    <Trash2 className="h-3.5 w-3.5" /> Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
          </>
        )}
      </main>
    </div>
  );
}
